import CompanyService from "../services/CompanyService";
import {
  availableLanguages,
  Language,
  TranslationService
} from "../services/TranslationService";
import { addTimezone } from "./ParseUtils";

export function addDots(x: any) {
  return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "0";
}

/*
 * Makes text no table to separate in multiple lines
 * Replaces spaces for &nbsp; (non-breaking space)
 */
export function unbreak(x: string) {
  return x.replaceAll(" ", "\u00A0");
}

export function capitalize(text: string | undefined | null) {
  if (!text || text.length < 1) {
    return "";
  }
  return text[0].toUpperCase() + text.substring(1);
}

export function uncapitalize(text: string | undefined | null) {
  if (!text || text.length < 1) {
    return "";
  }
  return text[0].toLowerCase() + text.substring(1);
}

export function extractDownloadFilename(
  contentDisposition: string | undefined,
  fallback: string
): string {
  if (!contentDisposition) {
    return fallback;
  }
  // Quoted form first: the filename can legally contain ";", so only the
  // closing quote (not the next ";") may terminate it.
  const quoted = contentDisposition.match(/filename="([^"]*)"/i);
  if (quoted) {
    return fixLatin1Mojibake(quoted[1]);
  }
  const unquoted = contentDisposition.match(/filename=([^;]+)/i);
  return unquoted ? fixLatin1Mojibake(unquoted[1].trim()) : fallback;
}

/*
 * The browser decodes response headers as Latin-1. When the server writes a UTF-8
 * filename directly into Content-Disposition, each UTF-8 byte arrives here as one
 * Latin-1 character (mojibake). Re-encoding as Latin-1 bytes and decoding as UTF-8
 * recovers the original name; genuinely ASCII/Latin-1 names are left untouched.
 */
function fixLatin1Mojibake(text: string): string {
  if (!hasLatin1SupplementChar(text)) {
    return text;
  }
  try {
    const bytes = Uint8Array.from(
      Array.from(text, (c) => c.charCodeAt(0) & 0xff)
    );
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return text;
  }
}

function hasLatin1SupplementChar(text: string): boolean {
  return Array.from(text).some((c) => {
    const code = c.charCodeAt(0);
    return code >= 0x80 && code <= 0xff;
  });
}
// export function formatDate(date: Date, format: string) {
//     let val = format.replace("yyyy", date.getFullYear().toString());
//     val = val.replace("MM", date.getMonth().toString().padStart(2, "0"));
//     val = val.replace("dd", date.getDate().toString().padStart(2, "0"));
//     return val;
// }

//"20231001"
export function formatIntizaDate(date: Date) {
  const formatter = new Intl.DateTimeFormat("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return formatter
    .formatToParts(date)
    .reverse()
    .map((x) => x.value)
    .join("")
    .replaceAll("/", "");
}

//"20231001 03:00"
export function formatIntizaDateTime(date: Date) {
  const newDate = addTimezone(date);
  return (
    formatIntizaDate(newDate) +
    " " +
    newDate.getHours().toString().padStart(2, "0") +
    ":" +
    newDate.getMinutes().toString().padStart(2, "0")
  );
}

//"2023-10-01"
export function formatIntizaOldDate(date: Date) {
  const formatter = new Intl.DateTimeFormat("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return formatter
    .formatToParts(date)
    .reverse()
    .map((x) => x.value)
    .join("")
    .replaceAll("/", "-");
}

/// 25/12/2022
export function formatDateDigits(date: Date, lang?: string) {
  return date.toLocaleDateString(lang ?? TranslationService.currentLanguage, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
}

/// 25/12/2022 12:00
export function formatDateTimeDigits(date: Date) {
  return date
    .toLocaleDateString(TranslationService.currentLanguage, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
    .replace(",", "");
}

/// 12:00
export function formatTimeIfNonZero(date: Date) {
  return date.getHours() !== 0 &&
    date.getMinutes() !== 0 &&
    date.getMilliseconds() !== 0
    ? date
        .toLocaleDateString(TranslationService.currentLanguage, {
          hour: "2-digit",
          minute: "2-digit"
        })
        .replace(",", "")
    : "";
}

/// 2022-12-25 yyyy-MM-dd formato para guardar los datos
export function formatDateDigitsMin(date?: string, lang?: string) {
  if (date && date !== "Invalid Date") {
    const dateToFormat = new Date(date);

    // Verificar que la fecha sea válida
    if (isNaN(dateToFormat.getTime())) {
      return "";
    }

    // Extraer año, mes y día directamente sin depender del locale
    const year = dateToFormat.getFullYear();
    const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
    const day = String(dateToFormat.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } else {
    return "";
  }
}

// recibe 2024-05-12 devuelve el forato fecha correcto segun funcion
export function formatAddDate(date: string, lang?: string, fn?: () => void) {
  const formatDate = new Date(date.replaceAll("-", "/"));
  return formatDateDigits(formatDate, lang);
}

/// 12:00:62
export function formatTimeLong(date: Date) {
  return date
    .toLocaleTimeString(TranslationService.currentLanguage, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })
    .replace(",", "");
}

/// 12:00
export function formatTimeShort(date: Date) {
  return date
    .toLocaleTimeString(TranslationService.currentLanguage, {
      hour: "2-digit",
      minute: "2-digit"
    })
    .replace(",", "");
}

/// 29 de junio
export function formatDateLong(date: Date) {
  return date.toLocaleDateString(TranslationService.currentLanguage, {
    day: "2-digit",
    month: "long"
  });
}

/// 29 jun 2022
export function formatDateShort(date: Date) {
  return date.toLocaleDateString(TranslationService.currentLanguage, {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

/// 29/01/22 12:00:00
export function formatDateTimeLong(date: Date) {
  return date
    .toLocaleDateString(TranslationService.currentLanguage, {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })
    .replace(",", "");
}

/// 29 jun 22 12:00
export function formatDateTimeShort(date: Date) {
  return date
    .toLocaleDateString(TranslationService.currentLanguage, {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    })
    .replace(",", "");
}

/// junio 2022
export function formatDateMonthYear(date: Date) {
  return (
    date.toLocaleDateString(TranslationService.currentLanguage, {
      month: "long"
    }) +
    " " +
    date.toLocaleDateString(TranslationService.currentLanguage, {
      year: "numeric"
    })
  );
}

/// jun-22
export function formatDateMonthYearShort(date: Date) {
  return (
    date.toLocaleDateString(TranslationService.currentLanguage, {
      month: "short"
    }) +
    "-" +
    date.toLocaleDateString(TranslationService.currentLanguage, {
      year: "2-digit"
    })
  );
}

/// $ 1.126.216(,53 depending if enabled)
export function formatCurrency(
  value: number | string,
  currencyId: number = CompanyService.getDefaultCurrencyId(),
  numberFormater = CompanyService.getCompanyAuth()?.ShowCents
    ? formatNumber
    : formatInteger
) {
  if (value === null || value === undefined) {
    return undefined;
  }
  const symbol = CompanyService.getCurrencies().find(
    (x) => x.CurrencyId === currencyId
  )?.Symbol;
  const valueData = numberFormater(parseFloat(value as string));
  const result = (symbol ?? "") + "\u00A0" + valueData;
  return result;
}

export function formatCurrencyConsolidated(
  value: number | string,
  currencySymbol: string = CompanyService.GetDefaultCurrencySymbol(),
  numberFormater = CompanyService.getCompanyAuth()?.ShowCents
    ? formatNumber
    : formatInteger
) {
  if (value === null || value === undefined) {
    return undefined;
  }
  const valueData = numberFormater(parseFloat(value as string));
  const result = (currencySymbol ?? "") + "\u00A0" + valueData;
  return result;
}

/// $ 1.126.216(,53 depending if enabled)
export function formatCurrencyPortal(
  value: number,
  currency: string,
  showCents: boolean
) {
  if (value === null || value === undefined) {
    return undefined;
  }
  return (
    (currency ?? "") +
    "\u00A0" +
    (showCents ? formatNumber : formatInteger)(value)
  );
}

/// 1.126.216,53
export function formatNumber(value: number) {
  return formatNumberFractions(value, 2);
}

export function formatNumberFractions(value: number, fractions: number) {
  value = parseFloat(value.toString());
  const tz = CompanyService.getCompanyToken()?.culture;
  const culture =
    tz && availableLanguages.includes(tz as Language)
      ? TranslationService.currentLanguage + "-AR"
      : tz;

  return value.toLocaleString(culture, {
    minimumFractionDigits: fractions,
    maximumFractionDigits: fractions
  });
}

/// 1.126.216
export function formatInteger(value: number) {
  if (value === 0) {
    return "0";
  }
  const tz = CompanyService.getCompanyToken()?.culture;
  const culture =
    tz && availableLanguages.includes(tz as Language)
      ? TranslationService.currentLanguage + "-AR"
      : tz;
  const result = value.toLocaleString(culture, { maximumFractionDigits: 0 });
  return result;
}

function addCommas(text: string) {
  let j = 3;
  let textLength = text.length;
  while (j < textLength) {
    text = `${text.slice(0, textLength - j)},${text.slice(textLength - j, textLength)}`;
    textLength++;
    j += 3 + 1;
  }
  return text;
}

export function exponentialToDecimal(exponential: number) {
  let decimal = exponential.toString().toLowerCase();
  if (decimal.includes("e+")) {
    const exponentialSplitted = decimal.split("e+");
    let postfix = "";
    for (
      let i = 0;
      i <
      +exponentialSplitted[1] -
        (exponentialSplitted[0].includes(".")
          ? exponentialSplitted[0].split(".")[1].length
          : 0);
      i++
    ) {
      postfix += "0";
    }
    decimal = addCommas(exponentialSplitted[0].replace(".", "") + postfix);
  }
  if (decimal.toLowerCase().includes("e-")) {
    const exponentialSplitted = decimal.split("e-");
    let prefix = "0.";
    for (let i = 0; i < +exponentialSplitted[1] - 1; i++) {
      prefix += "0";
    }
    decimal = prefix + exponentialSplitted[0].replace(".", "");
  }
  return decimal;
}

export function isMoreThan24Hours(oldDate: string | Date): boolean {
  const currentDate = new Date();
  const oldDateObj = new Date(oldDate);
  if (isNaN(oldDateObj.getTime())) {
    throw new Error("Invalid date format");
  }

  const differenceInMilliseconds = currentDate.getTime() - oldDateObj.getTime();
  const millisecondsIn24Hours = 24 * 60 * 60 * 1000;
  return differenceInMilliseconds > millisecondsIn24Hours;
}

export const isToday = (dateToCheck?: Date) => {
  const today = new Date();
  const oldDateObj = new Date(dateToCheck);

  if (isNaN(oldDateObj.getTime())) {
    throw new Error("Invalid date format");
  }

  return oldDateObj
    ? oldDateObj.getDate() === today.getDate() &&
        oldDateObj.getMonth() === today.getMonth() &&
        oldDateObj.getFullYear() === today.getFullYear()
    : false;
};

export function diffDaysFromDate(fecha: string) {
  const [dia, mes, anio] = fecha.split("/").map(Number);
  const fechaDada = new Date(anio, mes - 1, dia);
  const hoy: Date = new Date();

  const diferenciaMs = hoy.getTime() - fechaDada.getTime();
  const dias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));
  return dias;
}

export function diffDaysFromISOString(fechaIso: string): number {
  const fechaDada = new Date(fechaIso);
  const hoy = new Date();

  // Normalizamos ambas fechas a medianoche para evitar problemas con la hora
  const fechaDadaUTC = new Date(
    Date.UTC(fechaDada.getFullYear(), fechaDada.getMonth(), fechaDada.getDate())
  );
  const hoyUTC = new Date(
    Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
  );

  const diferenciaMs = fechaDadaUTC.getTime() - hoyUTC.getTime();
  return Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));
}

export const formatDate = (date: Date) =>
  formatDateDigits(date, "ja").replaceAll("/", "");
